// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Domain glossary. Every jargon term used in the UI has an entry here, surfaced
// as click-to-show info popovers via the .glossary-link spans (see about.ts).

export interface GlossaryEntry {
  term: string;
  definition: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  ipea: {
    term: 'IPEA',
    definition:
      'The Independent Parliamentary Expenses Authority — the statutory body that administers, reports and audits the work expenses of federal parliamentarians and their staff. It publishes this data every quarter.',
  },
  'work expenses': {
    term: 'Work expenses',
    definition:
      'Taxpayer-funded costs a parliamentarian may claim for carrying out their parliamentary, electorate and official duties — travel, offices, staff, cars and communications. This is separate from their salary.',
  },
  'travel allowance': {
    term: 'Travel Allowance',
    definition:
      'A set daily rate paid to a parliamentarian to cover accommodation and meals when they stay away from home overnight on official business (also called TA).',
  },
  comcar: {
    term: 'COMCAR',
    definition:
      'The Australian Government car-with-driver service used by parliamentarians and office-holders. Charges appear under "Other Car Costs".',
  },
  'private-plated vehicle': {
    term: 'Private-Plated Vehicle',
    definition:
      'A leased vehicle provided to a parliamentarian for private and official use, with the lease and running costs charged to their office.',
  },
  'office administration': {
    term: 'Office Administration',
    definition:
      'Day-to-day electorate-office running costs — consumables, printing and communications, publications and similar. Reported as itemised transactions.',
  },
  'office facilities': {
    term: 'Office Facilities',
    definition:
      'The cost of providing and fitting out electorate offices (leases, fit-out, maintenance). Reported by IPEA as a quarterly aggregate figure, not as individual line items.',
  },
  'employee travel': {
    term: 'Employee Travel',
    definition:
      'Travel expenses of the staff a parliamentarian employs. Reported by IPEA as a quarterly aggregate total per office, not as individual trips.',
  },
  'family travel': {
    term: 'Family Travel',
    definition:
      'Limited, entitlement-based travel for a parliamentarian’s designated family members, charged to the parliamentarian’s expenses.',
  },
  'scheduled commercial transport': {
    term: 'Scheduled Commercial Transport',
    definition: 'Fares on scheduled services — commercial flights, trains and the like.',
  },
  'unscheduled commercial transport': {
    term: 'Unscheduled Commercial Transport',
    definition: 'Charter flights, cabcharge and other on-demand transport.',
  },
  electorate: {
    term: 'Electorate (Division)',
    definition:
      'One of the 150 geographic areas that each elect a member to the House of Representatives. Senators represent a whole state or territory rather than a single electorate.',
  },
  homebase: {
    term: 'Home base',
    definition:
      'The location IPEA records as a parliamentarian’s home base, used to work out travel entitlements and allowances.',
  },
  chamber: {
    term: 'Chamber',
    definition:
      'Which house of Parliament a member sits in: the House of Representatives (MPs, one per electorate) or the Senate (Senators, representing states and territories).',
  },
  'reporting period': {
    term: 'Reporting period',
    definition:
      'The calendar quarter the expense was reported in (Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec). IPEA publishes each quarter about a month after it ends.',
  },
  adjustment: {
    term: 'Adjustment / repayment',
    definition:
      'A correction or money paid back by a parliamentarian, which appears in the data as a negative amount and reduces their net total.',
  },
  'former pm': {
    term: 'Former Prime Minister',
    definition:
      'Former PMs retain limited taxpayer-funded entitlements (such as some travel and office support) for life, reported here under a separate "Former PM" chamber.',
  },
};
